import { ChatSocketService } from '@agent/agent-interface/contact-view/chat/services/chat-socket.service';
import { IEpticaDeprecatedService } from '@agent/core/channel-digit/mail/eptica/eptica-deprecated.service';
import { isEmailValid } from '@agent/core/channel-digit/mail/util/mail-util';
import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { IContactStatusDeprecatedService } from '@agent/core/contact/contact-status-deprecated.service';
import { IConferenceDeprecatedService } from '@agent/core/phone/conference/conference-deprecated.service';
import { GuestState, IGuest } from '@agent/core/phone/conference/models/guest';
import { SessionDeprecatedService } from '@commons/core/auth/session-deprecated.service';
import { HttpDeprecatedService } from '@commons/core/transport/http-deprecated.service';
import { ChannelUtil } from '@commons/util/helper/channel';
import { RxUtil } from '@commons/util/utilities/rx';
import { IDeferred, ILogService, IPromise, IQService, isArray, isDefined, isObject, isString, toJson } from 'angular';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  from,
  map,
  mergeMap,
  Observable,
  of,
  OperatorFunction,
  pipe,
  switchMap,
  take,
  throwError,
} from 'rxjs';

import {
  BookingFacade,
  BookingStatus,
  ChanEnum,
  Channel,
  ChannelDirection,
  ChannelType,
  COMETD_CONFERENCE_IS_DELETED,
  COMETD_TRANSFER_REJECTED,
  Configuration,
  CSR_FACADE_PREFIX,
  ElasticSearchService,
  ForwardSkillType,
  IStateEvent,
  MessagingService,
  Option,
  PhoneValidationService,
  RequestId,
  Skill,
  StorageService,
  ToasterFacade,
} from '@contact/upgrade';

import { IExternalCenterInfo, IExternalCenterTransfer } from './models/external-transfer';
import {
  isResponseId,
  ITransfer,
  toKey,
  TransferHistorySelectionStatus,
  TransferKey,
  TransferResult,
  TransferTarget,
  TransferType,
} from './models/transfer';

//TODO to remove when it will not overuse dm2
const CHECK_AGENT_AVAILABILITY = true;

const MODULE_NAME = 'transferDeprecatedService';

const TRANSFERS_KEY = 'transfers';

export interface ITransferDeprecatedService {
  getAvailableSkills(key: TransferKey, checkForAgentAvailability?: boolean): IPromise<Skill[]>;
  createTransfer(key: TransferKey, channel: Channel, ignoreConference?: boolean): ITransfer;
  updateTransfer(transfer: ITransfer): void;
  getTransfer(key: TransferKey): Option<ITransfer>;
  canValidateTransfer(key: TransferKey, channelType?: ChannelType): boolean;
  validateTransfer(key: TransferKey): IPromise<any>;
  validateTransfer$(key: TransferKey): Observable<TransferResult>;
  cancelTransfer(key: TransferKey): IPromise<any>;
  isDialingTarget(key: TransferKey): boolean;
  retriveTransferTec(): Observable<IExternalCenterTransfer>;
  validateSkillAndAgentForAssistance(key: TransferKey, channelType?: ChannelType): boolean;
  updateHistorySelectionAbility(selectable: TransferHistorySelectionStatus): void;
  isHistorySelectionEnabled(): Observable<TransferHistorySelectionStatus>;
  toggleMailHistoryBlindOption(key: TransferKey, blind: boolean): void;
  getIncludedMessagesId(key: TransferKey): string[];
  checkMailHistory(key: TransferKey, messageId: string): void;
  uncheckMailHistory(key: TransferKey, messageId: string): void;
  changeMailHistory(key: TransferKey, messageId: string, check: boolean): void;
}

export class TransferDeprecatedService implements ITransferDeprecatedService {
  static $inject: Array<string> = [
    '$log',
    '$q',
    'messaging',
    'httpDeprecated',
    'storage',
    'conferenceDeprecatedService',
    'phoneValidation',
    'sessionDeprecatedService',
    'elasticSearchService',
    'contactDeprecatedService',
    'epticaDeprecatedService',
    'contactStatusDeprecatedService',
    'chatSocketService',
    'configuration',
    'contactFacade',
    'toasterFacade',
  ];

  public name = MODULE_NAME;

  private transfers: { [responseId: string]: ITransfer } = {};

  private _isHistorySelectable$: BehaviorSubject<TransferHistorySelectionStatus> = new BehaviorSubject(
    TransferHistorySelectionStatus.DISABLED
  );

  readonly checkAvailabilityWithoutMediaBlending: boolean;

  constructor(
    private readonly $log: ILogService,
    private readonly $q: IQService,
    private readonly messaging: MessagingService,
    private readonly httpDeprecated: HttpDeprecatedService,
    private readonly storage: StorageService,
    private readonly conferenceDeprecatedService: IConferenceDeprecatedService,
    private readonly phoneValidation: PhoneValidationService,
    private readonly sessionDeprecatedService: SessionDeprecatedService,
    private readonly elasticSearchService: ElasticSearchService,
    private readonly contactDeprecatedService: IContactDeprecatedService,
    private readonly epticaDeprecatedService: IEpticaDeprecatedService,
    private readonly contactStatusDeprecatedService: IContactStatusDeprecatedService,
    private readonly chatSocketService: ChatSocketService,
    private readonly configuration: Configuration,
    private readonly contactFacade: BookingFacade,
    private readonly toasterFacade: ToasterFacade
  ) {
    this.transfers = this.storage.get(TRANSFERS_KEY).getOrElse({});

    // contact state success
    this.contactStatusDeprecatedService.success().subscribe(data => this.contactStateListener(data));

    this.messaging
      .forRemote()
      .listen$<any>(COMETD_TRANSFER_REJECTED)
      .subscribe(dto => {
        this.getTransfer(dto.responseId).forEach(transfer => {
          transfer.rejected = true;
          let targets = this.conferenceDeprecatedService.getTargetGuests(dto.responseId);
          if (targets.length > 0) {
            targets[0].state = GuestState.INITIALIZED;
          }
          this.conferenceDeprecatedService.parkContact(dto.responseId);
        });
      });

    this.messaging
      .forRemote()
      .listen$<any>(COMETD_CONFERENCE_IS_DELETED)
      .pipe(
        switchMap(dto =>
          this.contactFacade.getById(dto.bookingUuid).pipe(
            take(1),
            map(booking => ({ dto, booking }))
          )
        )
      )
      .subscribe(({ dto, booking }) => {
        if (dto && dto.bookingUuid !== '') {
          if (booking) {
            this.displayTransferRejectedAlert(booking.channel);
          }
          if (booking && ChannelUtil.equals(booking.channel, ChanEnum.In.Chat)) {
            if (this.isDialingTarget(dto.bookingUuid)) {
              this.chatSocketService.manageRefusedTransfer(dto.bookingUuid);
            } else {
              this.chatSocketService.manageLeaveConference(dto.bookingUuid);
            }
          }
          this.conferenceDeprecatedService.getTargetGuests(dto.bookingUuid).forEach(guest => {
            this.conferenceDeprecatedService.removeGuest(guest.id);
          });
          this.removeTransfer(dto.bookingUuid);
          if (dto.hasOwnProperty('conferenceUuid') && dto.conferenceUuid !== '') {
            this.conferenceDeprecatedService.removeConference(dto.conferenceUuid);
          }
        }
      });

    if (!!this.configuration.checkAvailabilityWithoutMediaBlending) {
      this.checkAvailabilityWithoutMediaBlending = this.configuration.checkAvailabilityWithoutMediaBlending;
    } else {
      this.checkAvailabilityWithoutMediaBlending = false;
    }
  }

  private displayTransferRejectedAlert(channel: Channel) {
    // display alert by channel type
    if (ChannelUtil.equals(channel, ChanEnum.In.Phone) || ChannelUtil.equals(channel, ChanEnum.Unit.Phone)) {
      this.toasterFacade.notification({
        message: 'agent:contact.workspace.phone.transfer.toast.error',
      });
    }
  }

  private contactStateListener(event: IStateEvent<BookingStatus>) {
    let responseId = event.id;
    if (event.newState === BookingStatus.CLOSED) {
      this.removeTransfer(responseId);
    }
  }

  private buildECTransfer(data): IExternalCenterInfo[] {
    if (
      !!data &&
      isDefined(data.hits) &&
      isDefined(data.hits.total) &&
      isDefined(data.hits.total.value) &&
      data.hits.total.value > 0 &&
      isArray(data.hits.hits)
    ) {
      return data.hits.hits.map(hit => ({
        name: hit._source.name,
        virtualSkillUuid: hit._source.virtualSkillUuid,
      }));
    }
    return [];
  }

  getAvailableSkills(key: TransferKey, checkForAgentAvailability: boolean = true): IPromise<Skill[]> {
    if (isResponseId(key)) {
      return this.httpDeprecated
        .asPromise()
        .post(`${CSR_FACADE_PREFIX}/getSkillsForTransferToNewBooking`, [
          key,
          CHECK_AGENT_AVAILABILITY ? checkForAgentAvailability : false,
          CHECK_AGENT_AVAILABILITY ? this.checkAvailabilityWithoutMediaBlending : false,
        ])
        .then(success => {
          if (isArray(success.data)) {
            const skillDtos = success.data;
            return skillDtos.map(skillDto => {
              let cssClass = '';
              if (!skillDto.csrAvailable) {
                cssClass = 'red';
              } else {
                cssClass = 'green';
              }
              return {
                id: skillDto.uuid,
                name: skillDto.name,
                agentAvailable: skillDto.csrAvailable,
                agentAvailableOnDomain: skillDto.csrAvailableOnDomain,
                cssClass: cssClass,
                transferType: ForwardSkillType[skillDto.skillTransferType as string],
              } as Skill;
            });
          }
          return [];
        });
    } else {
      return this.getTransfer(key)
        .map(transfer =>
          this.httpDeprecated
            .asPromise()
            .post(CSR_FACADE_PREFIX + '/getSkillsInheritedOfDomainForChannel', [
              ChannelType[transfer.channel.type],
              ChannelDirection[transfer.channel.direction],
            ])
            .then(success => {
              if (isArray(success.data)) {
                const skillDtos = success.data;
                return skillDtos.map(skillDto => ({
                  id: skillDto.uuid,
                  name: skillDto.name,
                }));
              }
              return [];
            })
        )
        .getOrElse(this.$q.resolve([]));
    }
  }

  createTransfer(key: TransferKey, channel: Channel, ignoreConference?: boolean): ITransfer {
    let transfer = this.getTransfer(key);
    if (transfer.isDefined()) {
      this.$log.warn(`[transfer] already existing transfer with response id[${key}]. Returning it.`);
      return transfer.get();
    }
    let newTransfer: ITransfer = {
      key,
      channel,
      type: TransferType.ACCOMPANIED,
      targetType: TransferTarget.SKILL,
      comment: '',
      rejected: false,
      ignoreConference,
      sendKnowledgeBaseNotification: false,
    };
    this.transfers[toKey(key)] = newTransfer;
    this.$log.info(`[transfer] created transfer configuration for response with id[${key}].`);
    return newTransfer;
  }

  updateTransfer(transfer: ITransfer): void {
    this.getTransfer(transfer.key).forEach(t => {
      this.transfers[toKey(t.key)] = transfer;
      this.storage.store(TRANSFERS_KEY, this.transfers);
      this.$log.info(`[transfer] updated transfer configuration for response with id[${toKey(t.key)}].`);
    });
  }

  private removeTransfer(key: TransferKey): void {
    delete this.transfers[toKey(key)];
    this.storage.store(TRANSFERS_KEY, this.transfers);
    this.$log.info(`[transfer] removed transfer configuration for response with id[${toKey(key)}].`);
  }

  getTransfer(key: TransferKey): Option<ITransfer> {
    return Option.of(this.transfers[toKey(key)]).orElseFunc(() =>
      // if transfers map is empty, try to loads saved one
      this.storage.get(TRANSFERS_KEY).flatMap(transfers => Option.of(transfers[toKey(key)]))
    );
  }

  canValidateTransfer(key: TransferKey, channelType: ChannelType = ChannelType.PHONE): boolean {
    return (
      !this.isDialingTarget(key) &&
      this.getTransfer(key)
        .map(transfer => {
          if (channelType === ChannelType.MAIL) {
            if (!isString(transfer.comment) || transfer.comment.length === 0) {
              return false;
            }
          }
          switch (transfer.targetType) {
            case TransferTarget.SKILL:
              return isArray(transfer.target) && transfer.target.filter(el => el).length > 0;
            case TransferTarget.AGENT:
            case TransferTarget.AGENT_ASSISTANCE_BY_SKILL:
              return this.validateAgent(transfer, channelType);
            case TransferTarget.CORRESPONDENT:
              return this.validateCorrespondent(transfer, channelType);
            case TransferTarget.FREE_NUMBER:
              return this.validateFreeNumber(transfer, channelType);
            case TransferTarget.EXTERNAL_CENTER:
              return isObject(transfer.target);
            case TransferTarget.TEAM:
              return this.validateTeam(transfer, channelType);
            case TransferTarget.COMPOSITE:
              return this.validateComposite(transfer);
            default:
              return false;
          }
        })
        .getOrElse(false)
    );
  }

  validateComposite(transfer: ITransfer): boolean {
    const { target } = transfer;
    switch (true) {
      case isObject(target):
        return 'id' in target && 'type' in target && (target.type === 'agent' || target.type === 'contact' || target.type === 'directory');
      case isString(target):
        return true;
      default:
        return false;
    }
  }

  validateCorrespondent(transfer: ITransfer, channelType: ChannelType = ChannelType.PHONE): boolean {
    switch (channelType) {
      case ChannelType.PHONE:
        return isObject(transfer.target) && isString(transfer.target.id) && transfer.target.id.length > 0;
      case ChannelType.MAIL:
        return isObject(transfer.target) && isEmailValid(transfer.target.email);
      default:
        return true;
    }
  }

  validateTeam(transfer: ITransfer, channelType: ChannelType): boolean {
    let teamValid: boolean = isObject(transfer.target) && isString(transfer.target.id) && transfer.target.id.length > 0;
    if (channelType !== ChannelType.MAIL) {
      return teamValid;
    }
    // in case of mail check there is at least one skill selected
    return teamValid && transfer.target.skills.length > 0;
  }

  validateFreeNumber(transfer: ITransfer, channelType: ChannelType = ChannelType.PHONE): boolean {
    switch (channelType) {
      case ChannelType.PHONE:
        return isString(transfer.target) && transfer.target.length > 0 && this.phoneValidation.isValid(transfer.target);
      case ChannelType.MAIL:
        return isEmailValid(transfer.target);
      default:
        return true;
    }
  }

  validateAgent(transfer: ITransfer, channelType: ChannelType = ChannelType.PHONE): boolean {
    switch (channelType) {
      case ChannelType.PHONE:
      case ChannelType.CHAT:
        return isObject(transfer.target) && isString(transfer.target.id) && transfer.target.id.length > 0;
      case ChannelType.MAIL:
        return isObject(transfer.target) && isEmailValid(transfer.target.email);
      default:
        return true;
    }
  }

  /**
   * This method can olly be called by asking for help actions in mail interaction,
   * it is used to check if the agent is well chosen when user selected skill as target,
   * (referred to the commit 6b37ca40e176a337dec601525f67abac71d9be8e)
   * for other cases we pass the test
   * @param key key of transfer
   */
  validateSkillAndAgentForAssistance(key: TransferKey): boolean {
    return this.getTransfer(key)
      .map(transfer => {
        if (transfer.channel && transfer.channel.type === ChannelType.MAIL && transfer.targetType === TransferTarget.SKILL) {
          return isObject(transfer.target) && isEmailValid(transfer.target.email);
        }
        return true;
      })
      .getOrElse(false);
  }

  private transferToPhoneSkill(transfer: ITransfer): IPromise<any> {
    if (!isResponseId(transfer.key)) return this.$q.reject();
    let deferred: IDeferred<any> = this.$q.defer<any>();
    let key: string = transfer.key;
    let accompanied = transfer.type === TransferType.ACCOMPANIED;
    let data: any = {
      skillList: {
        skillUuids: (transfer.target as Skill[]).map(skill => skill.id),
      },
      previousSkillNames: [],
      requestingCsrName: this.sessionDeprecatedService.getProfile().firstname + ' ' + this.sessionDeprecatedService.getProfile().lastname,
      requestingCsrNickname: this.sessionDeprecatedService.getProfile().nickname ? this.sessionDeprecatedService.getProfile().nickname : '',
      comment: transfer.comment,
    };
    this.contactDeprecatedService
      .getContact(key)
      .then(contact => (data.previousSkillNames = contact.skills))
      .finally(() =>
        this.httpDeprecated
          .asPromise()
          .withConfig({
            headers: {
              'Content-Type': 'text/plain; charset=UTF-8',
            },
          })
          .withData(toJson(data))
          .post(CSR_FACADE_PREFIX + '/transferContactToNewBooking', [key, accompanied])
          .then(
            success => {
              this.$log.info(`[transfer] transfer of response with id[${key}] to skill(s) (accompanied[${accompanied}]) succeeded.`);
              deferred.resolve(success);
            },
            error => {
              this.$log.error(`[transfer] transfer of response with id[${key}] to skill(s) (accompanied[${accompanied}]) failed.`);
              deferred.reject(error);
            }
          )
      );

    return deferred.promise;
  }

  private transferToMailSkill$(transfer: ITransfer): Observable<TransferResult> {
    if (isResponseId(transfer.key)) return throwError(() => `key for transfer mail isn't correct`);
    return from(transfer.key).pipe(
      mergeMap((requestId: number) =>
        RxUtil.fromPromise(() =>
          this.epticaDeprecatedService.transferRequestToSkill(
            requestId,
            this.sessionDeprecatedService.getProfile().firstname + ' ' + this.sessionDeprecatedService.getProfile().lastname,
            transfer.target as Skill[],
            transfer.source as Skill[],
            transfer.comment
          )
        ).pipe(this.getReturnedRequestIds('[' + MODULE_NAME + '] multipleTransferToSkill', requestId))
      )
    );
  }

  private getReturnedRequestIds(prefixToLog: string, requestId: number): OperatorFunction<any, TransferResult> {
    return pipe(
      map(() => {
        this.$log.debug(prefixToLog + ' key[' + requestId + '] ok');
        return [requestId, true] as TransferResult;
      }),
      catchError(key => {
        this.$log.debug(prefixToLog + ' key[' + requestId + '] ERROR[' + key + ']');
        return of([requestId, false] as TransferResult);
      })
    );
  }

  private transferToSkill(transfer: ITransfer): IPromise<any> {
    switch (transfer.channel.type) {
      case ChannelType.PHONE:
      case ChannelType.CHAT:
      case ChannelType.UNITARY_PHONE:
      case ChannelType.SOCIAL_NETWORK:
        return this.transferToPhoneSkill(transfer);
      case ChannelType.MAIL:
        return RxUtil.toPromise(this.transferToMailSkill$(transfer), this.$q);
      default:
        return this.$q.reject();
    }
  }

  private tranferToEC(transfer: ITransfer): IPromise<any> {
    if (!isResponseId(transfer.key)) return this.$q.reject();
    let deferred: IDeferred<any> = this.$q.defer<any>();
    let key: string = transfer.key;
    let accompanied = true;
    this.$log.debug(transfer);
    let data: any = {
      skillList: {
        skillUuids: [transfer.target.virtualSkillUuid],
      },
      previousSkillNames: [],
      requestingCsrName: this.sessionDeprecatedService.getProfile().firstname + ' ' + this.sessionDeprecatedService.getProfile().lastname,
      requestingCsrNickname: this.sessionDeprecatedService.getProfile().nickname ? this.sessionDeprecatedService.getProfile().nickname : '',
      comment: transfer.comment,
    };
    this.contactDeprecatedService
      .getContact(key)
      .then(contact => (data.previousSkillNames = contact.skills))
      .finally(() =>
        this.httpDeprecated
          .asPromise()
          .withConfig({
            headers: {
              'Content-Type': 'text/plain; charset=UTF-8',
            },
          })
          .withData(toJson(data))
          .post(CSR_FACADE_PREFIX + '/transferContactToNewBooking', [key, accompanied])
          .then(
            success => {
              this.$log.info(`[transfer] transfer of response with id[${key}] to skill(s) (accompanied[${accompanied}]) succeeded.`);
              deferred.resolve(success);
            },
            error => {
              this.$log.error(`[transfer] transfer of response with id[${key}] to skill(s) (accompanied[${accompanied}]) failed.`);
              deferred.reject(error);
            }
          )
      );

    return deferred.promise;
  }

  private transferToPhoneFreeNumber(transfer: ITransfer): IPromise<any> {
    if (!isResponseId(transfer.key)) return this.$q.reject();
    let key: string = transfer.key;
    return this.httpDeprecated
      .asPromise()
      .post(CSR_FACADE_PREFIX + '/transferCall', [key, 'NONE', 'NONE', transfer.target, 'FREE_NUMBER'])
      .then(
        success => {
          this.$log.info(`[transfer] transfer of response with id[${key}] to free number (${transfer.target}).`);
          return this.$q.resolve(success);
        },
        error => {
          this.$log.error(`[transfer] transfer of response with id[${key}] to free number (${transfer.target}) failed.`);
          return this.$q.reject(error);
        }
      );
  }

  private transferToMailFreeEmail$(transfer: ITransfer): Observable<TransferResult> {
    if (isResponseId(transfer.key)) return throwError(() => `key for transfer mail isn't correct`);
    return from(transfer.key).pipe(
      mergeMap((requestId: number) =>
        this.epticaDeprecatedService
          .safeTransferRequestToEmail(requestId, transfer.target, transfer.comment)
          .pipe(this.getReturnedRequestIds('[' + MODULE_NAME + '] transferToMailFreeEmail', requestId))
      )
    );
  }

  private transferToFreeNumber(transfer: ITransfer): IPromise<any> {
    switch (transfer.channel.type) {
      case ChannelType.PHONE:
      case ChannelType.UNITARY_PHONE:
        return this.transferToPhoneFreeNumber(transfer);
      case ChannelType.MAIL:
        return RxUtil.toPromise(this.transferToMailFreeEmail$(transfer), this.$q);
      default:
        return this.$q.reject();
    }
  }

  private transferToPhoneCorrespondent(transfer: ITransfer): IPromise<any> {
    if (!isResponseId(transfer.key)) return this.$q.reject();
    let key: string = transfer.key;
    return this.httpDeprecated
      .asPromise()
      .post(CSR_FACADE_PREFIX + '/transferCall', [key, transfer.target.id, 'NONE', transfer.target.phoneNumber, 'DIRECTORY'])
      .then(
        success => {
          this.$log.info(
            `[transfer] transfer of response with id[${key}] to directory correspondent (${transfer.target.name}, ${transfer.target.phoneNumber}).`
          );
          return success;
        },
        error => {
          this.$log.error(
            `[transfer] transfer of response with id[${key}] to directory correspondent (${transfer.target.name}, ${transfer.target.phoneNumber}) failed.`
          );
          return error;
        }
      );
  }

  private transferToMailCorrespondent$(transfer: ITransfer): Observable<TransferResult> {
    if (isResponseId(transfer.key)) {
      return throwError(() => `key for transfer mail isn't correct`);
    }
    return from(transfer.key).pipe(
      mergeMap((requestId: number) =>
        this.epticaDeprecatedService
          .safeTransferRequestToEmail(requestId, transfer.target.email as string, transfer.comment)
          .pipe(this.getReturnedRequestIds('[' + MODULE_NAME + '] multipleTransferToCorrespondent', requestId))
      )
    );
  }

  private transferToCorrespondent(transfer: ITransfer): IPromise<any> {
    switch (transfer.channel.type) {
      case ChannelType.PHONE:
      case ChannelType.UNITARY_PHONE:
        return this.transferToPhoneCorrespondent(transfer);
      case ChannelType.MAIL:
        return RxUtil.toPromise(this.transferToMailCorrespondent$(transfer), this.$q);
      default:
        return this.$q.reject();
    }
  }

  private transferToPhoneAgentOrTeam(transfer: ITransfer): IPromise<any> {
    if (!isResponseId(transfer.key)) return this.$q.reject();
    let key: string = transfer.key;
    let data: any = {
      skillList: {
        skillUuids: [],
      },
      targetedUuid: transfer.target.id,
      previousSkillNames: [],
      requestingCsrName: this.sessionDeprecatedService.getProfile().firstname + ' ' + this.sessionDeprecatedService.getProfile().lastname,
      requestingCsrNickname: this.sessionDeprecatedService.getProfile().nickname ? this.sessionDeprecatedService.getProfile().nickname : '',
      comment: transfer.comment,
      ignoreConference: transfer.ignoreConference,
      targetType: TransferTarget[transfer.targetType],
      targetName: transfer.target.name,
    };

    if (isDefined(transfer.target) && isDefined(transfer.target.skills)) {
      data.skillList.skillUuids = transfer.target.skills.map(s => s.id);
    }

    let deferred: IDeferred<any> = this.$q.defer<any>();

    this.contactDeprecatedService
      .getContact(key)
      .then(contact => (data.previousSkillNames = contact.skills))
      .finally(() =>
        this.httpDeprecated
          .asPromise()
          .withConfig({
            headers: {
              'Content-Type': 'text/plain; charset=UTF-8',
            },
          })
          .withData(toJson(data))
          .post(CSR_FACADE_PREFIX + '/transferContactToNewCsr', [key])
          .then(
            success => {
              deferred.resolve(success);
            },
            error => {
              deferred.reject(error);
            }
          )
      );

    return deferred.promise;
  }

  private transferToMailAgent$(transfer: ITransfer): Observable<TransferResult> {
    if (isResponseId(transfer.key)) return throwError(() => `key for transfer mail isn't correct`);

    return this.epticaDeprecatedService.getCsrEsIdObs(transfer.target.uuid).pipe(
      switchMap((esId: number) => this.epticaDeprecatedService.getCsrIasIdObs(esId)),
      switchMap((iasId: string) =>
        from(transfer.key).pipe(
          mergeMap((requestId: RequestId) =>
            this.epticaDeprecatedService
              .safeTransferRequestToAgent(requestId, transfer.target, transfer.comment, iasId)
              .pipe(this.getReturnedRequestIds('[' + MODULE_NAME + '] transferToMailAgent', +requestId))
          )
        )
      )
    );
  }

  private transferToAgent(transfer: ITransfer): IPromise<any> {
    switch (transfer.channel.type) {
      case ChannelType.PHONE:
      case ChannelType.CHAT:
      case ChannelType.UNITARY_PHONE:
      case ChannelType.SOCIAL_NETWORK:
        return this.transferToPhoneAgentOrTeam(transfer);
      case ChannelType.MAIL:
        return RxUtil.toPromise(this.transferToMailAgent$(transfer), this.$q);
      default:
        return this.$q.reject();
    }
  }

  private transferToMailTeam$(transfer: ITransfer): Observable<TransferResult> {
    if (isResponseId(transfer.key)) return throwError(() => `key for transfer mail isn't correct`);

    let destinationSkillUuids: Array<Skill> = [];

    if (isDefined(transfer.target) && isDefined(transfer.target.skills)) {
      destinationSkillUuids = transfer.target.skills;
    }

    return from(transfer.key).pipe(
      mergeMap((requestId: number) =>
        RxUtil.fromPromise(() =>
          this.epticaDeprecatedService.transferRequestToTeam(
            requestId,
            this.sessionDeprecatedService.getProfile().firstname + ' ' + this.sessionDeprecatedService.getProfile().lastname,
            transfer.target,
            transfer.comment,
            transfer.source,
            destinationSkillUuids
          )
        ).pipe(this.getReturnedRequestIds('[' + MODULE_NAME + '] multipleTransferToSkill', requestId))
      )
    );
  }

  private transferToTeam(transfer: ITransfer): IPromise<any> {
    switch (transfer.channel.type) {
      case ChannelType.PHONE:
      case ChannelType.CHAT:
      case ChannelType.UNITARY_PHONE:
        return this.transferToPhoneAgentOrTeam(transfer);
      case ChannelType.MAIL:
        return RxUtil.toPromise(this.transferToMailTeam$(transfer), this.$q);
      default:
        return this.$q.reject();
    }
  }

  validateTransfer(key: TransferKey): IPromise<any> {
    return this.getTransfer(key)
      .map(transfer => {
        // storing transfer configuration in way to retrieve it when F5 key is pressed
        this.storage.store(TRANSFERS_KEY, this.transfers);
        switch (transfer.targetType) {
          case TransferTarget.SKILL:
            return this.transferToSkill(transfer);
          case TransferTarget.AGENT:
          case TransferTarget.AGENT_ASSISTANCE_BY_SKILL:
            return this.transferToAgent(transfer);
          case TransferTarget.CORRESPONDENT:
            return this.transferToCorrespondent(transfer);
          case TransferTarget.FREE_NUMBER:
            return this.transferToFreeNumber(transfer);
          case TransferTarget.EXTERNAL_CENTER:
            return this.tranferToEC(transfer);
          case TransferTarget.TEAM:
            return this.transferToTeam(transfer);
          default:
            return this.$q.reject();
        }
      })
      .getOrElseFunc(() => this.$q.reject());
  }

  validateTransfer$(key: TransferKey): Observable<TransferResult> {
    return this.getTransfer(key)
      .map(transfer => {
        // storing transfer configuration in way to retrieve it when F5 key is pressed
        this.storage.store(TRANSFERS_KEY, this.transfers);
        switch (transfer.channel.type) {
          case ChannelType.MAIL:
            switch (transfer.targetType) {
              case TransferTarget.SKILL:
                return this.transferToMailSkill$(transfer);
              case TransferTarget.AGENT:
                return this.transferToMailAgent$(transfer);
              case TransferTarget.CORRESPONDENT:
                return this.transferToMailCorrespondent$(transfer);
              case TransferTarget.FREE_NUMBER:
                return this.transferToMailFreeEmail$(transfer);
              case TransferTarget.TEAM:
                return this.transferToMailTeam$(transfer);
              default:
                return EMPTY;
            }
          default:
            return RxUtil.fromPromise(() => this.validateTransfer(key)).pipe(
              map(() => [key as string, true] as TransferResult),
              catchError(() => of([key as string, false] as TransferResult))
            );
        }
      })
      .getOrElse(EMPTY);
  }

  private cancelTransferToSkill(transfer: ITransfer, targuetGuest: IGuest): IPromise<any> {
    if (!isResponseId(transfer.key)) {
      return this.$q.reject();
    }
    let key: string = transfer.key;
    return this.httpDeprecated
      .asPromise()
      .post(CSR_FACADE_PREFIX + '/cancelTransfertContactToNewBooking', [targuetGuest.id])
      .then(
        success => {
          this.$log.info(`[transfer] transfer of response with id[${key}] to skill(s) cancelled.`);
          this.conferenceDeprecatedService.unparkContactAndAgent(key);
          return success;
        },
        error => {
          this.$log.error(`[transfer] failed to cancel transfer of response with id[${key}] to skill(s).`);
          return error;
        }
      );
  }

  cancelTransfer(key: TransferKey): IPromise<any> {
    if (isResponseId(key)) {
      return this.getTransfer(key)
        .map(transfer => {
          const targetGuests = this.conferenceDeprecatedService.getTargetGuests(key);
          // transfer is not accompanied or is accompanied but target is initialized
          if (
            transfer.type === TransferType.NOT_ACCOMPANIED ||
            targetGuests.length === 0 ||
            (targetGuests.length > 0 && targetGuests[0].state === GuestState.INITIALIZED)
          ) {
            let guestStateSt: string = targetGuests.length > 0 ? GuestState[targetGuests[0].state] : 'null';
            this.$log.debug(
              '[transfer] Ignore cancel on transfer for responseId[' +
                key +
                '] type[' +
                TransferType[transfer.type] +
                '] + targetGuests nb[' +
                targetGuests.length +
                '] targetGuests[0].state[' +
                guestStateSt +
                ']'
            );
            return this.$q.resolve();
          }
          // this is a transfer to skill and it is accompanied and target is currently joining the conference
          if (transfer.targetType === TransferTarget.SKILL && (targetGuests.length > 0 || targetGuests[0].state === GuestState.JOINING)) {
            return this.cancelTransferToSkill(transfer, targetGuests[0]);
          }

          return this.conferenceDeprecatedService.cancelTransfer(key);
        })
        .getOrElseFunc(() => this.$q.reject())
        .finally(() => {
          this.conferenceDeprecatedService.getTargetGuests(key).forEach(guest => {
            this.conferenceDeprecatedService.removeGuest(guest.id);
          });
          this.removeTransfer(key);
        });
    }
    return this.$q.resolve();
  }

  retriveTransferTec(): Observable<any> {
    let isTransfert: boolean = false;
    let domainUuid = this.sessionDeprecatedService.getProfile().domainId;
    let searchParams: any = {
      types: ['external_center'],
      size: 1000,
      query: {
        bool: {
          must: [
            {
              term: { domainUuid: domainUuid },
            },
          ],
          must_not: [
            {
              term: { isTransfert: isTransfert },
            },
          ],
        },
      },
    };

    // do the request
    this.$log.info('Get External Center info ');
    return this.elasticSearchService.searchOnSeveralTypesDeprecated(searchParams).pipe(
      map(data => ({
        total: data.hits.total.value,
        externalCenterToTransfer: this.buildECTransfer(data),
      })),
      catchError(() => throwError(() => new Error(`Failed to retrieve External Center info`)))
    );
  }

  isDialingTarget(key: TransferKey): boolean {
    if (isResponseId(key)) {
      const targets = this.conferenceDeprecatedService.getTargetGuests(key);
      if (targets.length > 0) {
        return targets[0].state === GuestState.JOINING;
      }
    }
    return false;
  }

  isHistorySelectionEnabled(): Observable<TransferHistorySelectionStatus> {
    return this._isHistorySelectable$.asObservable();
  }

  updateHistorySelectionAbility(selectable: TransferHistorySelectionStatus): void {
    this._isHistorySelectable$.next(selectable);
  }

  toggleMailHistoryBlindOption(key: TransferKey, blind: boolean): void {
    this.getTransfer(key).forEach(transfer => {
      transfer.blind = blind;
      this.$log.info(`[transfer] updated ask for help history blind option with id[${toKey(key)}].`);
    });
    this.storage.store(TRANSFERS_KEY, this.transfers);
  }

  // check the messageid for the transfer
  checkMailHistory(key: TransferKey, messageId: string): void {
    this.changeMailHistory(key, messageId, true);
  }

  // uncheck the messageid for the transfer
  uncheckMailHistory(key: TransferKey, messageId: string): void {
    this.changeMailHistory(key, messageId, false);
  }

  changeMailHistory(key: TransferKey, messageId: string, check: boolean): void {
    this.getTransfer(key).forEach(transfer => {
      if (!transfer.includedMessagesId) {
        transfer.includedMessagesId = [];
      }
      const idx = transfer.includedMessagesId.indexOf(messageId);
      if (check) {
        if (idx === -1 && messageId.trim().length > 0) {
          transfer.includedMessagesId.push(messageId);
        }
      } else {
        if (idx > -1) {
          transfer.includedMessagesId.splice(idx, 1);
        }
      }
    });
    this.storage.store(TRANSFERS_KEY, this.transfers);
  }

  getIncludedMessagesId(key: TransferKey): string[] {
    let _ret: string[] = [];
    this.getTransfer(key).forEach(transfer => {
      _ret = _ret.concat(transfer.includedMessagesId || []);
    });
    return _ret;
  }
}

export default MODULE_NAME;
